import AuthContext from "common/store/AuthContext";

import { ReportData } from "../types/ReportData";
import { ApiReportData } from "../types/ApiReportData";
import { useCallback, useContext } from "react";
import { generateErrorMsg, getServerUrl } from "utils/commonTools";
import axios from "axios";
import { GetBasicResponse } from "common/types/responses/BasicResponse";
import { safeApiDate, toJapaneseDate } from "utils/DateTools";
import { MapArea } from "common/types/MapArea";
import { ChartOverlayData } from "common/components/map/common/osm/components/ChartOverlayDrawer";
import { PinData } from "common/components/map/common/osm/components/PinDrawer";
import { Task, WorkReports } from "pages/workreport/types/WorkReportData";
import { useMapOverlay } from "./useMapOverlay";

export const useMapWork = () => {
  const authCtxt = useContext(AuthContext);
  const { token } = authCtxt;
  const { addToOverlay } = useMapOverlay();

  const searchData = useCallback(
    async (
      parkId: number,
      from: Date,
      to: Date,
    ): Promise<{ succeeded: boolean; msg?: string; data?: ReportData<WorkReports>[] }> => {
      const config = {
        headers: { "Content-type": "application/json", Authorization: `Bearer ${token}` },
      };
      try {
        // 日報データの取得
        const url =
          getServerUrl() +
          `report/${parkId}/workReports?from=${toJapaneseDate(from)}&to=${toJapaneseDate(to)}`;

        const { data } = await axios.get(url, config);
        const reportData = data as GetBasicResponse<ApiReportData<WorkReports>[]>;
        if (!reportData) {
          return { succeeded: false, msg: "取得したデータに異常がありました" };
        }

        const normalized = (reportData.data ?? []).map((item) => ({
          ...item,
          date: safeApiDate(item.date),
        }));

        authCtxt.updateToken(reportData.token, new Date(reportData.expiresAt));

        return {
          succeeded: true,
          msg: "",
          data: normalized,
        };
      } catch (error) {
        var errMsg = generateErrorMsg(error);
        return { succeeded: false, msg: errMsg };
      }
    },
    [authCtxt, token],
  );

  const convertToTasksOverlays = (
    data: ReportData<WorkReports>[],
    metaAreas: MapArea[],
  ): ChartOverlayData[] => {
    const overlayMap = new Map<number, ChartOverlayData>();

    for (const dt of data) {
      dt.value.reports.forEach((report) => {
        // 期間ごとに集計
        const category = report.period;

        report.tasks.forEach((task: Task) => {
          const count = 1;

          // エリアで指定（複数あり）
          if (task.areas && task.areas.length > 0) {
            task.areas.forEach((area) => {
              addToOverlay(overlayMap, area, category, count, metaAreas);
            });
          }
          // ポイントで指定
          else if (task.point) {
            const area = task.point.area;
            const pin: PinData = { point: task.point.coords, label: task.point.title };
            if (area) addToOverlay(overlayMap, area, category, count, metaAreas, pin);
            else
              addToOverlay(
                overlayMap,
                { id: -2, name: "位置のみ" },
                category,
                count,
                metaAreas,
                pin,
              );
          }
          // 指定なし
          else addToOverlay(overlayMap, { id: -1, name: "地区なし" }, category, count, metaAreas);
        });
      });
    }

    overlayMap.forEach((x) => x.bars.sort((a, b) => (a.label.id < b.label.id ? -1 : 1)));
    return Array.from(overlayMap.values());
  };

  return { searchData, convertToTasksOverlays };
};
